import {
  createContext,
  PropsWithChildren,
  ReactNode,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { AuthenticatedUser, useAuth } from '@/lib/auth';

type FilterContextType = {
  organizationId: string | null;
  setOrganizationId: (id: string) => void;
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

type FilterProviderProps = {
  children: ReactNode;
  value?: FilterContextType;
};

const StaffOrganizationIdContextProvider = ({
  authenticatedUser,
  organizationId: currentOrganizationId,
  setOrganizationId,
}: {
  authenticatedUser: AuthenticatedUser;
  organizationId: string | null;
  setOrganizationId: (id: string) => void;
}) => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const isStaff = authenticatedUser.user?.is_staff === true;
    if (!isStaff) return;

    const urlOrganizationId = searchParams.get('organizationId');
    if (urlOrganizationId && urlOrganizationId !== currentOrganizationId) {
      setOrganizationId(urlOrganizationId);
    }
  }, [searchParams, currentOrganizationId, setOrganizationId, authenticatedUser]);

  return null;
};

const FilterProviderWithAuth = ({ children }: PropsWithChildren) => {
  const { authenticatedUser } = useAuth();
  const [organizationId, setOrganizationIdRaw] = useState<string | null>(null);
  const [lastInitializedUser, setLastInitializedUser] = useState<AuthenticatedUser['user']>(null);

  if (authenticatedUser.user && authenticatedUser.user !== lastInitializedUser) {
    const isStaff = authenticatedUser.user.is_staff === true;

    setLastInitializedUser(authenticatedUser.user);
    setOrganizationIdRaw(
      isStaff
        ? (new URLSearchParams(window.location.search).get('organizationId') ?? null)
        : (authenticatedUser.user.membership?.organization?.id ?? null),
    );
  }

  const setOrganizationId = useCallback((id: string) => {
    startTransition(() => setOrganizationIdRaw(id));
  }, []);

  const contextValue = useMemo(() => ({ organizationId, setOrganizationId }), [organizationId, setOrganizationId]);

  return (
    <FilterContext.Provider value={contextValue}>
      <StaffOrganizationIdContextProvider
        authenticatedUser={authenticatedUser}
        organizationId={organizationId}
        setOrganizationId={setOrganizationId}
      />
      {children}
    </FilterContext.Provider>
  );
};

export const FilterProvider = ({ children, value }: FilterProviderProps) => {
  if (value) {
    return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
  }

  return <FilterProviderWithAuth>{children}</FilterProviderWithAuth>;
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);

  if (context === undefined) {
    throw new Error('useFilterContext must be used within an FilterProvider');
  }

  return context;
};
